import { useState } from "react";
import { ArrowLeft, ShieldCheck, CreditCard, Sparkles } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import PriceBreakdown from "./PriceBreakdown";
import { openRazorpayCheckout } from "@/lib/razorpay/client";
import type {
  BookingDraft,
  CreateOrderPayload,
  CreateOrderResponse,
} from "@/types/booking";
import { formatCurrency, formatDate } from "@/lib/utils/formatters";
import { MEAL_PLANS } from "@/types/rooms";

interface StepPaymentProps {
  draft: BookingDraft;
  onBack: () => void;
}

export default function StepPayment({ draft, onBack }: StepPaymentProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const grandTotal = (draft.pricing?.total ?? 0) + draft.addonsTotal;

  const mealPlanMeta = draft.mealPlan
    ? MEAL_PLANS.find((p) => p.key === draft.mealPlan)
    : null;

  const handlePay = async () => {
    if (!draft.pricing || !draft.guest) return;

    setLoading(true);
    setError("");

    try {
      const functions = getFunctions(undefined, "asia-south1");
      const createOrder = httpsCallable<
        CreateOrderPayload,
        CreateOrderResponse
      >(functions, "createOrder");

      const { data } = await createOrder({
        roomTypeId: draft.roomTypeId,
        checkIn: draft.checkIn,
        checkOut: draft.checkOut,
        adults: draft.adults,
        children: draft.children,
        guest: draft.guest,
        addons: draft.addons,
        mealPlan: draft.mealPlan ?? undefined,
      });

      await openRazorpayCheckout({
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: "Hotel Metropolis",
        description: `${draft.roomTypeName} · ${draft.pricing.nights} nights · ${draft.mealPlan ?? "EP"}`,
        order_id: data.orderId,
        prefill: {
          name: `${draft.guest.firstName} ${draft.guest.lastName}`,
          email: draft.guest.email,
          contact: draft.guest.phone,
        },
        theme: { color: "#0d4a2c" },
        handler: (_response) => {
          navigate(
            `/booking/confirmation?ref=${data.bookingRef}&id=${data.bookingId}`,
          );
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      });
    } catch (err) {
      console.error("Payment error:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  if (!draft.pricing || !draft.guest) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-metro-green-950 mb-1">
          Review & Pay
        </h2>
        <p className="text-sm text-metro-ink-subtle">
          Review your booking details before completing payment.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Left — review */}
        <div className="space-y-4">
          {/* Booking summary */}
          <section className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
              Booking Summary
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Room</p>
                <p className="font-medium text-metro-green-950">
                  {draft.roomTypeName}
                </p>
              </div>
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Guests</p>
                <p className="font-medium text-metro-green-950">
                  {draft.adults} {draft.adults === 1 ? "Adult" : "Adults"}
                  {draft.children > 0 &&
                    `, ${draft.children} ${draft.children === 1 ? "Child" : "Children"}`}
                </p>
              </div>
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Check-in</p>
                <p className="font-medium text-metro-green-950">
                  {formatDate(draft.checkIn)}
                </p>
              </div>
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">
                  Check-out
                </p>
                <p className="font-medium text-metro-green-950">
                  {formatDate(draft.checkOut)}
                </p>
              </div>
              {mealPlanMeta && (
                <div className="col-span-2">
                  <p className="text-metro-ink-subtle text-xs mb-0.5">
                    Meal Plan
                  </p>
                  <p className="font-medium text-metro-green-950">
                    {mealPlanMeta.label}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Add-ons summary */}
          {draft.addons.length > 0 && (
            <section className="rounded-xl border border-gray-100 bg-white p-5 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
                Add-ons
              </h3>
              <div className="space-y-2">
                {draft.addons.map((a) => (
                  <div
                    key={a.addonId}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="flex items-center gap-1.5 text-metro-ink-muted">
                      <Sparkles
                        size={12}
                        className="text-metro-gold shrink-0"
                      />
                      <span className="truncate">{a.name}</span>
                      {a.quantity > 1 && (
                        <span className="shrink-0 text-xs text-metro-ink-subtle">
                          × {a.quantity}
                        </span>
                      )}
                    </span>
                    <span className="ml-2 shrink-0 font-medium text-metro-green-950">
                      {formatCurrency(a.lineTotal)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Guest summary */}
          <section className="rounded-xl border border-gray-100 bg-white p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
              Guest Details
            </h3>
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Name</p>
                <p className="font-medium text-metro-green-950">
                  {draft.guest.firstName} {draft.guest.lastName}
                </p>
              </div>
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Email</p>
                <p className="truncate font-medium text-metro-green-950">
                  {draft.guest.email}
                </p>
              </div>
              <div>
                <p className="text-metro-ink-subtle text-xs mb-0.5">Phone</p>
                <p className="font-medium text-metro-green-950">
                  {draft.guest.phone}
                </p>
              </div>
              {draft.guest.specialRequests && (
                <div className="sm:col-span-2">
                  <p className="text-metro-ink-subtle text-xs mb-0.5">
                    Special Requests
                  </p>
                  <p className="font-medium text-metro-green-950">
                    {draft.guest.specialRequests}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Mobile price summary */}
          <div className="lg:hidden">
            <PriceBreakdown
              pricing={draft.pricing}
              roomTypeName={draft.roomTypeName}
              checkIn={draft.checkIn}
              checkOut={draft.checkOut}
              addons={draft.addons}
              addonsTotal={draft.addonsTotal}
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-metro-ink-subtle">
            <ShieldCheck size={14} className="text-metro-green-800 shrink-0" />
            Secured by Razorpay · 256-bit SSL encryption
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="flex justify-between border-t border-gray-100 pt-4">
            <Button
              variant="outline"
              leftIcon={<ArrowLeft size={16} />}
              onClick={onBack}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              leftIcon={<CreditCard size={16} />}
              onClick={handlePay}
              disabled={loading}
            >
              {loading ? "Processing…" : `Pay ${formatCurrency(grandTotal)}`}
            </Button>
          </div>
        </div>

        {/* Right — price summary, desktop only */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <PriceBreakdown
              pricing={draft.pricing}
              roomTypeName={draft.roomTypeName}
              checkIn={draft.checkIn}
              checkOut={draft.checkOut}
              addons={draft.addons}
              addonsTotal={draft.addonsTotal}
            />
          </div>
        </div>
      </div>
    </div>
  );
}